import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
const MODEL_NAME_ALLOWED_REGEX = /^[a-zA-Z0-9\s\-_\(\)]*$/;
const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;

export const validateModelName = (name, isUserRole = false) => {
  const raw = String(name || "");
  const trimmedName = raw.trim().replace(/\s+/g, " ");

  if (!raw || !trimmedName) {
    return isUserRole ? "Please select a front image file" : "Name is required";
  }
  if (trimmedName.length < 3) {
    return "Name must be at least 3 characters";
  }
  if (trimmedName.length > 100) {
    return "Name can be at most 100 characters";
  }
  if (!MODEL_NAME_ALLOWED_REGEX.test(raw)) {
    return "Name can only contain letters, numbers, spaces, -, _, and ()";
  }
  if (!CONTAINS_LETTER_REGEX.test(trimmedName)) {
    return "Name must contain at least one letter";
  }
  return "";
};

export const validateModelGender = (gender) => {
  if (!gender || !String(gender).trim()) {
    return "Please select a gender";
  }
  if (!["men", "women"].includes(String(gender).toLowerCase())) {
    return "Invalid gender selected";
  }
  return "";
};

export const validateModelType = (sType, isUserRole = false) => {
  if (isUserRole) return "";
  if (!sType || !String(sType).trim()) {
    return "Please select a type";
  }
  if (!["model", "user"].includes(String(sType).toLowerCase())) {
    return "Invalid type selected";
  }
  return "";
};

export const useModelUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  defaultGender = "",
  defaultSType = "",
  modelToEdit = null,
}) => {
  const userRole = useSelector((state) => state.auth?.role);
  const isUserRole = userRole === "user";
  const isEditMode = Boolean(modelToEdit);

  const [formData, setFormData] = useState({
    sName: "",
    gender: "",
    sType: isUserRole ? "user" : (defaultSType || ""),
    frontImage: null,
    backImage: null,
  });

  const [errors, setErrors] = useState({});
  const [previewUrls, setPreviewUrls] = useState({ front: null, back: null });
  const [zoomImage, setZoomImage] = useState(null);
  const formRef = useRef(null);

  const getDisplayNameFromFileName = (fileName) => {
    if (!fileName) return "";
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) return fileName;
    return fileName.substring(0, lastDotIndex);
  };

  const validateFileName = (file, modelName, isBackImage = false) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
      return { valid: false, error: `Image must be in jpg, jpeg, or png format. Got: ${fileExtension}` };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds 50MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
      };
    }

    const lowerName = file.name.toLowerCase();

    if (!isBackImage) {
      if (lowerName.includes("back")) {
        return {
          valid: false,
          error: "Filename contains 'back'. Please upload it to the Back View field",
        };
      }
    } else {
      if (lowerName.includes("front")) {
        return {
          valid: false,
          error: "Filename contains 'front'. Please upload it to the Front View field",
        };
      }
    }

    if (isUserRole) {
      return { valid: true };
    }

    // Allow user to pick and preview image first; validate name match once modelName is entered or on submit
    if (!modelName || !modelName.trim()) {
      return { valid: true };
    }

    const normalizedModelName = modelName.trim().replace(/\s+/g, "").toLowerCase();
    const expectedFileName = isBackImage
      ? `${normalizedModelName}_back.${fileExtension}`
      : `${normalizedModelName}.${fileExtension}`;

    if (file.name.toLowerCase() !== expectedFileName.toLowerCase()) {
      return {
        valid: false,
        error: isBackImage
          ? `Expected file name: ${normalizedModelName}_back.jpg/.jpeg/.png`
          : `Expected file name: ${normalizedModelName}.${fileExtension}`,
      };
    }

    return { valid: true };
  };

  const prevModelRef = useRef(modelToEdit);
  const isFirstMountRef = useRef(true);

  const resetForm = useCallback(() => {
    if (modelToEdit) {
      const rawGender = String(modelToEdit.gender || defaultGender || "").trim().toLowerCase();
      const normalizedGender = rawGender === "men" || rawGender === "women" ? rawGender : "";
      const rawType = String(isUserRole ? "user" : (modelToEdit.sType || modelToEdit.type || defaultSType || "")).trim().toLowerCase();
      const normalizedType = ["model", "user"].includes(rawType) ? rawType : (isUserRole ? "user" : "");

      setFormData({
        sName: modelToEdit.sName || modelToEdit.name || modelToEdit.title || "",
        gender: normalizedGender,
        sType: normalizedType,
        frontImage: null,
        backImage: null,
      });
      setPreviewUrls({
        front: modelToEdit.front || modelToEdit.image || modelToEdit.images?.[0] || null,
        back: modelToEdit.back || modelToEdit.images?.[1] || null,
      });
    } else {
      setFormData({
        sName: "",
        gender: defaultGender || "",
        sType: isUserRole ? "user" : (defaultSType || ""),
        frontImage: null,
        backImage: null,
      });
      setPreviewUrls({ front: null, back: null });
    }
    setErrors({});
    setZoomImage(null);
  }, [modelToEdit, defaultGender, defaultSType, isUserRole]);

  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      if (modelToEdit) {
        resetForm();
      }
      return;
    }

    const prevModel = prevModelRef.current;
    const prevId = prevModel?.iModelId ?? prevModel?.id ?? prevModel?.modelId ?? null;
    const currId = modelToEdit?.iModelId ?? modelToEdit?.id ?? modelToEdit?.modelId ?? null;

    const isModelChanged = prevId !== currId || Boolean(prevModel) !== Boolean(modelToEdit);

    if (isModelChanged) {
      prevModelRef.current = modelToEdit;
      resetForm();
    }
  }, [modelToEdit, resetForm]);

  const hasChanges = useMemo(() => {
    if (!isEditMode) return true;
    const currentName = formData.sName.trim();
    const originalName = (modelToEdit?.sName || modelToEdit?.name || modelToEdit?.title || "").trim();
    const currentGender = formData.gender;
    const rawOrigGender = String(modelToEdit?.gender || defaultGender || "").trim().toLowerCase();
    const originalGender = rawOrigGender === "men" || rawOrigGender === "women" ? rawOrigGender : "";
    const currentSType = isUserRole ? "user" : formData.sType;
    const rawOrigType = String(isUserRole ? "user" : (modelToEdit?.sType || modelToEdit?.type || defaultSType || "")).trim().toLowerCase();
    const originalSType = isUserRole ? "user" : (["model", "user"].includes(rawOrigType) ? rawOrigType : "");

    const nameChanged = currentName !== originalName;
    const genderChanged = currentGender !== originalGender;
    const typeChanged = currentSType !== originalSType;
    const imageChanged = Boolean(formData.frontImage) || Boolean(formData.backImage);

    return nameChanged || genderChanged || typeChanged || imageChanged;
  }, [isEditMode, formData, modelToEdit, defaultGender, defaultSType, isUserRole]);

  const revalidateSelectedImages = (modelName, currentFrontImage, currentBackImage) => {
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };

      if (currentFrontImage) {
        const frontValidation = validateFileName(currentFrontImage, modelName, false);
        nextErrors.frontImage = frontValidation.valid ? "" : frontValidation.error;
      }

      if (currentBackImage) {
        const backValidation = validateFileName(currentBackImage, modelName, true);
        nextErrors.backImage = backValidation.valid ? "" : backValidation.error;
      }

      return nextErrors;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue =
      name === "sName"
        ? value.replace(/^\s+/, "").replace(/\s{2,}/g, " ")
        : value;

    setFormData((prev) => {
      const updated = { ...prev, [name]: sanitizedValue };

      if (name === "sName") {
        revalidateSelectedImages(sanitizedValue, prev.frontImage, prev.backImage);
      }

      return updated;
    });

    if (name === "sName") {
      const nameError = validateModelName(sanitizedValue, isUserRole);
      setErrors((prev) => ({ ...prev, sName: nameError }));
    } else if (name === "gender") {
      const genderError = validateModelGender(sanitizedValue);
      setErrors((prev) => ({ ...prev, gender: genderError }));
    } else if (name === "sType") {
      const typeError = validateModelType(sanitizedValue, isUserRole);
      setErrors((prev) => ({ ...prev, sType: typeError }));
    } else if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    if (name === "sName") {
      const nameError = validateModelName(value, isUserRole);
      setErrors((prev) => ({ ...prev, sName: nameError }));
    } else if (name === "gender") {
      const genderError = validateModelGender(value);
      setErrors((prev) => ({ ...prev, gender: genderError }));
    } else if (name === "sType") {
      const typeError = validateModelType(value, isUserRole);
      setErrors((prev) => ({ ...prev, sType: typeError }));
    }
  };

  const isFormValid = useMemo(() => {
    const nameToValidate = isUserRole
      ? getDisplayNameFromFileName(formData.frontImage?.name || formData.sName)
      : formData.sName;
    const nameError = validateModelName(nameToValidate, isUserRole);
    if (nameError) return false;

    const genderError = validateModelGender(formData.gender);
    if (genderError) return false;

    const typeError = validateModelType(formData.sType, isUserRole);
    if (typeError) return false;

    if (!isEditMode && !formData.frontImage) return false;
    if (isEditMode && !formData.frontImage && !previewUrls.front) return false;

    if (errors.frontImage || errors.backImage || errors.sName || errors.gender || errors.sType) {
      return false;
    }

    if (
      formData.frontImage &&
      formData.backImage &&
      formData.frontImage.name === formData.backImage.name &&
      formData.frontImage.size === formData.backImage.size
    ) {
      return false;
    }

    return true;
  }, [formData, previewUrls.front, errors, isUserRole, isEditMode]);

  const handleImageChange = (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    const fieldName = imageType === "front" ? "frontImage" : "backImage";
    const otherFieldName = imageType === "front" ? "backImage" : "frontImage";
    const otherFile = formData[otherFieldName];

    const derivedName = isUserRole && imageType === "front"
      ? getDisplayNameFromFileName(file.name)
      : formData.sName;
    const validation = validateFileName(file, derivedName, imageType === "back");

    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, [fieldName]: validation.error }));
      e.target.value = "";
      return;
    }

    if (otherFile && otherFile.name === file.name && otherFile.size === file.size) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "Front and Back images cannot be the same file",
      }));
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrls((prev) => ({ ...prev, [imageType]: reader.result }));
    };
    reader.readAsDataURL(file);

    setFormData((prev) => ({
      ...prev,
      sName: derivedName || prev.sName,
      [fieldName]: file,
      ...(isUserRole ? { sType: "user" } : {}),
    }));

    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleRemoveImage = (imageType) => {
    const fieldName = imageType === "front" ? "frontImage" : "backImage";
    setFormData((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
    setPreviewUrls((prev) => ({
      ...prev,
      [imageType]: isEditMode && imageType === "front"
        ? (modelToEdit?.front || modelToEdit?.image || modelToEdit?.images?.[0] || null)
        : null,
    }));

    const inputElement = document.getElementById(imageType === "front" ? "frontImageModal" : "backImageModal");
    if (inputElement) {
      inputElement.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const nameToValidate = isUserRole
      ? getDisplayNameFromFileName(formData.frontImage?.name || formData.sName)
      : formData.sName;

    const nameError = validateModelName(nameToValidate, isUserRole);
    if (nameError) {
      newErrors.sName = nameError;
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!isEditMode && !formData.frontImage) {
      newErrors.frontImage = "Front image is required";
    } else if (formData.frontImage) {
      const frontValidation = validateFileName(formData.frontImage, nameToValidate, false);
      if (!frontValidation.valid) newErrors.frontImage = frontValidation.error;
    }

    if (formData.backImage) {
      const backValidation = validateFileName(formData.backImage, nameToValidate, true);
      if (!backValidation.valid) newErrors.backImage = backValidation.error;
    }

    if (
      formData.frontImage &&
      formData.backImage &&
      formData.frontImage.name === formData.backImage.name &&
      formData.frontImage.size === formData.backImage.size
    ) {
      newErrors.backImage = "Front and Back images cannot be the same file";
    }

    const sTypeToValidate = isUserRole ? "user" : formData.sType;
    if (!sTypeToValidate) {
      newErrors.sType = "Type is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstErrorKey = Object.keys(newErrors)[0];
        const errorElement = formRef.current?.querySelector(`[name="${firstErrorKey}"], #${firstErrorKey}`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus({ preventScroll: true });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const rawName = isUserRole
      ? getDisplayNameFromFileName(formData.frontImage?.name || formData.sName)
      : formData.sName;
    const finalName = rawName.trim().replace(/\s+/g, " ");
    const finalSType = isUserRole ? "user" : formData.sType;
    const modelId = modelToEdit?.iModelId ?? modelToEdit?.id ?? modelToEdit?.modelId;

    if (isEditMode) {
      const formDataToSend = new FormData();
      formDataToSend.append("sName", finalName);
      formDataToSend.append("gender", formData.gender);
      formDataToSend.append("sType", finalSType);
      if (formData.frontImage) {
        formDataToSend.append("frontImage", formData.frontImage);
      }
      if (formData.backImage) {
        formDataToSend.append("backImage", formData.backImage);
      }
      onSubmit(formDataToSend, true, modelId);
    } else {
      const formDataToSend = new FormData();
      formDataToSend.append("sName", finalName);
      formDataToSend.append("gender", formData.gender);
      formDataToSend.append("sType", finalSType);
      formDataToSend.append("frontImage", formData.frontImage);
      if (formData.backImage) {
        formDataToSend.append("backImage", formData.backImage);
      }
      onSubmit(formDataToSend, false, null);
    }
  };

  const isFormDirty = useMemo(() => {
    if (isEditMode) {
      return hasChanges;
    }
    return Boolean(
      (formData.sName && formData.sName.trim()) ||
        (formData.gender && formData.gender !== defaultGender) ||
        (isUserRole ? false : Boolean(formData.sType && formData.sType !== defaultSType)) ||
        formData.frontImage ||
        formData.backImage ||
        Object.keys(errors).length > 0
    );
  }, [isEditMode, hasChanges, formData, defaultGender, defaultSType, isUserRole, errors]);

  const handleReset = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleCancel = useCallback(() => {
    resetForm();
    if (onClose) {
      onClose();
    }
  }, [resetForm, onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e && e.target !== e.currentTarget) return;
      if (onClose) {
        onClose();
      }
    },
    [onClose]
  );

  const handleClose = handleCancel;

  return {
    userRole,
    isUserRole,
    isEditMode,
    hasChanges,
    isFormValid,
    isFormDirty,
    modelToEdit,
    formData,
    setFormData,
    errors,
    previewUrls,
    zoomImage,
    setZoomImage,
    formRef,
    handleInputChange,
    handleInputBlur,
    handleImageChange,
    handleRemoveImage,
    handleSubmit,
    handleClose,
    handleReset,
    handleCancel,
    handleBackdropClick,
  };
};
